/** The capability classes, cheapest first. */
export const MODEL_TIERS = ['nano', 'mini', 'standard', 'flagship'];
/**
 * What OpenAI takes. PDF is not a separate capability, since parsing one requires a vision-capable
 * model, so it travels with the image support. This is a provider-level ceiling rather than a
 * per-model guess: `@ai-sdk/openai` throws `UnsupportedFunctionalityError` for a file part of any
 * other type, so no OpenAI row can accept more than this whatever the model does.
 *
 * `image/jpg` is not a typo and not a real IANA type. It is what browsers and our own upload
 * allow-list (`CHAT_UPLOAD_MIME_TYPES` in `@inference.engine/file-utils`) emit for some .jpg
 * files, and a media type absent from this list is withheld silently.
 */
const OPENAI_INPUTS = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/gif',
    'application/pdf',
];
/**
 * Anthropic takes everything OpenAI does, and additionally reads a `text/plain` file part, which
 * `@ai-sdk/anthropic` sends as a text `document` block where `@ai-sdk/openai` throws. Our upload
 * allow-list permits `text/plain`, so sharing OpenAI's list here withheld a file Claude reads.
 */
const ANTHROPIC_INPUTS = [...OPENAI_INPUTS, 'text/plain'];
/**
 * Gemini is the only family taking audio and video, declared by top-level segment because Google
 * documents `video/mov` where a browser sends `video/quicktime`, and our upload allow-list carries
 * `video/ogg`, which its table omits; both play fine through the File API. `application/pdf` is
 * exact because Google is explicit that document vision only meaningfully understands PDFs, and
 * `text` covers the types it flattens to plain text.
 */
const GEMINI_INPUTS = ['image', 'audio', 'video', 'application/pdf', 'text'];
/** OpenAI's GPT-5.6 family and GPT-6 Luna: `none` turns reasoning off. Medium is the default. */
const OPENAI_EFFORT = {
    levels: ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
    recommended: 'medium',
};
/** GPT-6.1 Sol and GPT-6 Astra, which do not take `none`. Astra's docs name no default. */
const OPENAI_EFFORT_NO_NONE = {
    levels: ['low', 'medium', 'high', 'xhigh', 'max'],
    recommended: 'medium',
};
/**
 * Claude 5.5. Opus and Haiku default to medium; Sonnet defaults to high on the API, but Anthropic
 * advises starting chat at medium, which is what a person in the chat is doing.
 */
const CLAUDE_EFFORT = {
    levels: ['low', 'medium', 'high', 'xhigh', 'max'],
    recommended: 'medium',
};
/** Gemini thinking levels, sent as `thinkingLevel`; none of the models here takes `minimal`. */
const GEMINI_LEVELS = ['low', 'medium', 'high'];
/**
 * Gemini 3.6 to 3.8 Flash: Google's introductory rate through 2026, then its standard rate. Vertex
 * bills the same on its global endpoint and 10% more on a regional one.
 */
const GEMINI_FLASH_PRICING = [
    { input: 0.75, output: 3.75, cachedInput: 0.075 },
    { from: '2027-01-01T00:00:00Z', input: 1.5, output: 7.5, cachedInput: 0.15 },
];
/**
 * Every model the platform knows, whether or not it is still offered.
 *
 * A superseded generation is retired by setting `userSelectable: false`, never by deleting its
 * row. Agents already name these ids, and this table is what supplies their `samplingSupport`: a
 * missing row falls back to permissive, which would start sending `temperature` to a reasoning
 * model that rejects it.
 *
 * The `userSelectable` rows are the curated list every picker offers, in display order, so they
 * come first and the first is the default. Agents offer them through {@link selectableModels}.
 * A request still naming a model an agent no longer offers runs on the agent's default instead of
 * failing (`offeredModel` in `apps/api/src/relay/turn-agent.ts`), which is why dropping a model
 * from the curated list does not strand the chats already on it.
 */
export const MODELS = [
    {
        key: 'gpt-6.1-sol',
        label: 'GPT-6.1 Sol',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 2, output: 10, cachedInput: 0.1, cacheWrite: 2.5 },
        reasoningEffort: OPENAI_EFFORT_NO_NONE,
    },
    {
        key: 'gpt-6.1-terra',
        label: 'GPT-6.1 Terra',
        provider: 'openai',
        tier: 'standard',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 1, output: 5, cachedInput: 0.05, cacheWrite: 1.25 },
        // The API takes `none`, but `@ai-sdk/openai` drops it for this id with a warning and runs the
        // default level, so offering it would silently do nothing.
        reasoningEffort: OPENAI_EFFORT_NO_NONE,
    },
    {
        key: 'gpt-6-astra',
        label: 'GPT-6 Astra',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 10, output: 50, cachedInput: 1 },
        reasoningEffort: OPENAI_EFFORT_NO_NONE,
    },
    {
        key: 'gpt-5.6-sol',
        label: 'GPT-5.6 Sol',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 5, output: 30, cachedInput: 0.5 },
        reasoningEffort: OPENAI_EFFORT,
    },
    {
        key: 'gpt-5.6-terra',
        label: 'GPT-5.6 Terra',
        provider: 'openai',
        tier: 'standard',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 2.5, output: 15, cachedInput: 0.25 },
        reasoningEffort: OPENAI_EFFORT,
    },
    {
        key: 'gpt-5.6-luna',
        label: 'GPT-5.6 Luna',
        provider: 'openai',
        tier: 'nano',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 1, output: 6, cachedInput: 0.1 },
        reasoningEffort: OPENAI_EFFORT,
    },
    {
        key: 'gpt-6-luna',
        label: 'GPT-6 Luna',
        provider: 'openai',
        tier: 'nano',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 0.1, output: 0.5, cachedInput: 0.01 },
        reasoningEffort: OPENAI_EFFORT,
    },
    {
        key: 'claude-sonnet-5-5',
        label: 'Claude Sonnet 5.5',
        provider: 'anthropic',
        tier: 'standard',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 2, output: 10, cachedInput: 0.2, cacheWrite: 2.5 },
        reasoningEffort: CLAUDE_EFFORT,
    },
    {
        key: 'claude-opus-5-5',
        label: 'Claude Opus 5.5',
        provider: 'anthropic',
        tier: 'flagship',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 4, output: 20, cachedInput: 0.2, cacheWrite: 5 },
        reasoningEffort: CLAUDE_EFFORT,
    },
    {
        key: 'claude-haiku-5-5',
        label: 'Claude Haiku 5.5',
        provider: 'anthropic',
        tier: 'nano',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: true,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        // The rate up to 100K input tokens. Past that it bills $0.50 / $2.50, which this flat shape
        // cannot express, so estimates read from here under-state long prompts; Langfuse has both tiers.
        pricing: { input: 0.1, output: 0.5, cachedInput: 0.01, cacheWrite: 0.125 },
        reasoningEffort: CLAUDE_EFFORT,
    },
    {
        // Served on Vertex only from `global`, not from a regional endpoint.
        key: 'gemini-4-argon',
        label: 'Gemini 4 Argon',
        provider: 'google',
        tier: 'flagship',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: true,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: [
            { input: 2, output: 10, cachedInput: 0.1 },
            { from: '2026-12-01T00:00:00Z', input: 4, output: 20, cachedInput: 0.2 },
        ],
        // Google names no default level.
        reasoningEffort: { levels: GEMINI_LEVELS, recommended: 'medium' },
    },
    {
        key: 'gemini-3.8-flash',
        label: 'Gemini 3.8 Flash',
        provider: 'google',
        tier: 'standard',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: true,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: GEMINI_FLASH_PRICING,
        reasoningEffort: { levels: GEMINI_LEVELS, recommended: 'medium' },
    },
    {
        key: 'gemini-3.1-pro-preview',
        label: 'Gemini 3.1 Pro',
        provider: 'google',
        tier: 'flagship',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: true,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        // The rate up to 200K prompt tokens. Past that it bills $4 / $18, which this shape cannot
        // express, so estimates read from here under-state long prompts; Langfuse has both tiers.
        pricing: { input: 2, output: 12, cachedInput: 0.2 },
        reasoningEffort: { levels: GEMINI_LEVELS, recommended: 'high' },
    },
    {
        key: 'gpt-5.1',
        label: 'GPT-5.1',
        provider: 'openai',
        tier: 'standard',
        contextWindow: 400_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'gpt-5.2',
        label: 'GPT-5.2',
        provider: 'openai',
        tier: 'standard',
        contextWindow: 400_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'gpt-5.4',
        label: 'GPT-5.4',
        provider: 'openai',
        tier: 'standard',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 2.5, output: 15, cachedInput: 0.25 },
    },
    {
        key: 'gpt-5.4-pro',
        label: 'GPT-5.4 pro',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 30, output: 180 },
    },
    {
        key: 'gpt-5.4-mini',
        label: 'GPT-5.4 mini',
        provider: 'openai',
        tier: 'mini',
        contextWindow: 400_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 0.75, output: 4.5, cachedInput: 0.08 },
    },
    {
        key: 'gpt-5.4-nano',
        label: 'GPT-5.4 nano',
        provider: 'openai',
        tier: 'nano',
        contextWindow: 400_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 0.2, output: 1.25, cachedInput: 0.02 },
    },
    {
        key: 'gpt-5.5',
        label: 'GPT-5.5',
        provider: 'openai',
        tier: 'standard',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 5, output: 30, cachedInput: 0.5 },
    },
    {
        key: 'gpt-5.5-pro',
        label: 'GPT-5.5 pro',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 30, output: 180 },
    },
    {
        key: 'gpt-6-sol',
        label: 'GPT-6 Sol',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 1_050_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 2, output: 10, cachedInput: 0.2 },
    },
    {
        // Retired (OpenAI sunset it). Kept as a non-selectable row so agents that persisted this id
        // still resolve their sampling support. Unlike the gpt-5.x series, GPT-4.1 supported
        // temperature and top_p sampling.
        key: 'gpt-4.1',
        label: 'GPT-4.1',
        provider: 'openai',
        tier: 'standard',
        contextWindow: 1_047_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: OPENAI_INPUTS,
        features: ['vision'],
        pricing: { input: 2, output: 8, cachedInput: 0.5 },
    },
    {
        key: 'o1',
        label: 'o1',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 200_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'o1-mini',
        label: 'o1-mini',
        provider: 'openai',
        tier: 'mini',
        contextWindow: 128_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'o1-preview',
        label: 'o1-preview',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 128_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'o3',
        label: 'o3',
        provider: 'openai',
        tier: 'flagship',
        contextWindow: 200_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'o3-mini',
        label: 'o3-mini',
        provider: 'openai',
        tier: 'mini',
        contextWindow: 200_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'o4-mini',
        label: 'o4-mini',
        provider: 'openai',
        tier: 'mini',
        contextWindow: 200_000,
        samplingSupport: 'none',
        userSelectable: false,
    },
    {
        key: 'claude-opus-5',
        label: 'Claude Opus 5',
        provider: 'anthropic',
        tier: 'flagship',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 5, output: 25, cachedInput: 0.5, cacheWrite: 6.25 },
    },
    {
        key: 'claude-sonnet-5',
        label: 'Claude Sonnet 5',
        provider: 'anthropic',
        tier: 'standard',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 3, output: 15, cachedInput: 0.3, cacheWrite: 3.75 },
    },
    {
        key: 'claude-sonnet-4-5@20250929',
        label: 'Claude Sonnet 4.5',
        provider: 'anthropic',
        tier: 'standard',
        contextWindow: 200_000,
        samplingSupport: 'temperature-or-topP',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 3, output: 15, cachedInput: 0.3, cacheWrite: 3.75 },
    },
    {
        key: 'claude-sonnet-4-6',
        label: 'Claude Sonnet 4.6',
        provider: 'anthropic',
        tier: 'standard',
        contextWindow: 1_000_000,
        samplingSupport: 'temperature-or-topP',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 3, output: 15, cachedInput: 0.3, cacheWrite: 3.75 },
    },
    {
        key: 'claude-opus-4-8',
        label: 'Claude Opus 4.8',
        provider: 'anthropic',
        tier: 'flagship',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 5, output: 25, cachedInput: 0.5, cacheWrite: 6.25 },
    },
    {
        key: 'claude-opus-4-7',
        label: 'Claude Opus 4.7',
        provider: 'anthropic',
        tier: 'flagship',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 5, output: 25, cachedInput: 0.5, cacheWrite: 6.25 },
    },
    {
        // The dated id is Anthropic's Google Cloud spelling and what the old runtime persists; the
        // dateless one is its API alias, which the Relay catalog uses.
        key: 'claude-haiku-4-5@20251001',
        aliases: ['claude-haiku-4-5'],
        label: 'Claude Haiku 4.5',
        provider: 'anthropic',
        tier: 'nano',
        contextWindow: 200_000,
        samplingSupport: 'temperature-or-topP',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        modelId: 'claude-haiku-4-5',
        features: ['reasoning', 'vision'],
        pricing: { input: 1, output: 5, cachedInput: 0.1, cacheWrite: 1.25 },
    },
    {
        key: 'claude-fable-5',
        label: 'Claude Fable 5',
        provider: 'anthropic',
        tier: 'flagship',
        contextWindow: 1_000_000,
        samplingSupport: 'none',
        userSelectable: false,
        acceptsMediaTypes: ANTHROPIC_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 10, output: 50, cachedInput: 1, cacheWrite: 12.5 },
    },
    {
        key: 'gemini-3.7-flash',
        label: 'Gemini 3.7 Flash',
        provider: 'google',
        tier: 'standard',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: GEMINI_FLASH_PRICING,
    },
    {
        key: 'gemini-3.6-flash',
        label: 'Gemini 3.6 Flash',
        provider: 'google',
        tier: 'standard',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: GEMINI_FLASH_PRICING,
    },
    {
        key: 'gemini-3.1-flash-lite',
        label: 'Gemini 3.1 Flash-Lite',
        provider: 'google',
        tier: 'nano',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 0.25, output: 1.5, cachedInput: 0.025 },
    },
    {
        key: 'gemini-3-flash-preview',
        label: 'Gemini 3 Flash',
        provider: 'google',
        tier: 'standard',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 0.5, output: 3, cachedInput: 0.05 },
    },
    {
        key: 'gemini-3.5-flash',
        label: 'Gemini 3.5 Flash',
        provider: 'google',
        tier: 'standard',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: GEMINI_INPUTS,
        features: ['reasoning', 'vision'],
        pricing: { input: 1.5, output: 9, cachedInput: 0.15 },
    },
    {
        key: 'gemini-2.5-flash',
        label: 'Gemini 2.5 Flash',
        provider: 'google',
        tier: 'standard',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: GEMINI_INPUTS,
    },
    {
        key: 'gemini-2.5-pro',
        label: 'Gemini 2.5 Pro',
        provider: 'google',
        tier: 'flagship',
        contextWindow: 1_048_576,
        samplingSupport: 'both',
        userSelectable: false,
        acceptsMediaTypes: GEMINI_INPUTS,
    },
];
function exactCapability(modelId) {
    return MODELS.find((m) => m.key === modelId || m.aliases?.includes(modelId));
}
/**
 * The registry entry for a model id: an exact key or alias, else the LONGEST key the id contains,
 * so `gpt-5.4-mini-2026-02-01` resolves to `gpt-5.4-mini` and not to `gpt-5.4`.
 */
export function getModelCapability(modelId) {
    if (!modelId)
        return undefined;
    const exact = exactCapability(modelId);
    if (exact)
        return exact;
    return MODELS.filter((m) => modelId.includes(m.key)).sort((a, b) => b.key.length - a.key.length)[0];
}
/** What effort a model can be asked for, from its registry row. */
export function reasoningEffortFor(modelId) {
    return getModelCapability(modelId)?.reasoningEffort;
}
/**
 * The key an id is an exact spelling of, which is what the model picker persists and so the name
 * the original runtime gives the model everywhere, cost reporting included.
 *
 * Exact only, unlike {@link getModelCapability}: the model an id merely contains is a relative, and
 * naming a call after it would attribute it to a different model.
 */
export function canonicalModelKey(modelId) {
    return exactCapability(modelId)?.key;
}
export function getUserSelectableModels() {
    return MODELS.filter((m) => m.userSelectable);
}
/**
 * What an agent with a model picker offers: the curated models in the given tiers, in display
 * order, so the first is the default. An agent never lists models by hand, which is what kept
 * superseded ones on offer.
 */
export function selectableModels({ tiers, }) {
    return getUserSelectableModels()
        .filter((m) => tiers.includes(m.tier))
        .map(({ key, provider }) => ({ key, provider }));
}
export function supportsTemperature(modelId) {
    const capability = getModelCapability(modelId);
    if (!capability)
        return true;
    return capability.samplingSupport !== 'none';
}
export function supportsTopP(modelId) {
    const capability = getModelCapability(modelId);
    if (!capability)
        return true;
    return capability.samplingSupport !== 'none';
}
export function supportsBothSamplingParams(modelId) {
    const capability = getModelCapability(modelId);
    if (!capability)
        return true;
    return capability.samplingSupport === 'both';
}
/** See {@link ModelCapability.acceptsMediaTypes} for what `undefined` means. */
export function acceptedMediaTypesFor(modelId) {
    return getModelCapability(modelId)?.acceptsMediaTypes;
}
function topLevelSegment(mediaType) {
    return mediaType.split('/')[0] ?? mediaType;
}
/**
 * Whether the given model can accept video attachments.
 *
 * Unlike the send path, a model with no established limit reports `false`: this answer picks a
 * model to switch a user's video onto, and claiming unconfirmed support routes the video to a
 * model that drops it.
 */
export function isVideoCapableModel(modelId) {
    const accepted = acceptedMediaTypesFor(modelId);
    return accepted !== undefined && accepted.some((t) => topLevelSegment(t) === 'video');
}
/**
 * Picks the first video-capable model from a candidate list, preserving the
 * caller's ordering (so the agent's preferred/first-enabled model wins). Use
 * with an agent's `enabledModels` to find a fallback to auto-switch to when a
 * user attaches a video to an incompatible model. Returns `undefined` when no
 * candidate supports video.
 */
export function findVideoCapableModel(candidateIds) {
    return candidateIds.find((id) => isVideoCapableModel(id));
}
